'use client';

import { forwardRef, useId, type TextareaHTMLAttributes } from 'react';

import { Label } from '../../primitives/label';
import { Textarea } from '../../primitives/textarea';
import { cn } from '../../utils/cn';

export interface UzTextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  hint?: string;
  error?: string;
  required?: boolean;
  /** Show a live character count below the field. Uses maxLength if set. */
  showCount?: boolean;
  containerClassName?: string;
}

export const UzTextarea = forwardRef<HTMLTextAreaElement, UzTextareaProps>(
  (
    {
      id: providedId,
      label,
      hint,
      error,
      required,
      showCount,
      containerClassName,
      className,
      disabled,
      value,
      defaultValue,
      maxLength,
      ...rest
    },
    ref,
  ) => {
    const autoId = useId();
    const id = providedId ?? autoId;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;
    const length =
      typeof value === 'string'
        ? value.length
        : typeof defaultValue === 'string'
          ? defaultValue.length
          : 0;

    return (
      <div className={cn('space-y-1.5', containerClassName)}>
        {label && (
          <Label htmlFor={id} className={cn(disabled && 'opacity-60')}>
            {label}
            {required && (
              <span className="text-destructive ml-1 align-middle text-base leading-none font-semibold">
                *
              </span>
            )}
          </Label>
        )}
        <Textarea
          id={id}
          ref={ref}
          disabled={disabled}
          value={value}
          defaultValue={defaultValue}
          maxLength={maxLength}
          aria-invalid={!!error || undefined}
          aria-describedby={[hintId, errorId].filter(Boolean).join(' ') || undefined}
          className={cn(error && 'border-destructive focus-visible:ring-destructive', className)}
          {...rest}
        />
        <div className="flex items-start justify-between gap-3">
          {/* `error || (hint && …)` short-circuited to the raw string whenever an
              error was set, so the message rendered unstyled and without its id. */}
          {(error || hint) && (
            <div className="min-h-[1rem]">
              {error ? (
                <p id={errorId} className="text-destructive text-xs font-medium">
                  {error}
                </p>
              ) : (
                <p id={hintId} className="text-muted-foreground text-xs">
                  {hint}
                </p>
              )}
            </div>
          )}
          {showCount && (
            <span className="text-muted-foreground text-xs tabular-nums">
              {length}
              {maxLength ? ` / ${maxLength}` : ''}
            </span>
          )}
        </div>
      </div>
    );
  },
);
UzTextarea.displayName = 'UzTextarea';
